<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { isToday } from "date-fns";
import { useInterval } from "@vueuse/core";
import VText from "@magnit/core/src/components/VText/VText.vue";
import VIcon from "@magnit/icons/src/components/VIcon/VIcon.vue";
import SaleIcon from "@magnit/icons/src/assets/icons/sale-24.svg";
import WarningIcon from "@magnit/icons/src/assets/icons/circle-warning.svg";
import { formatDateToDDMMMM, getDayOfWeekString } from "../utils/date";

const props = withDefaults(
  defineProps<{
    end: string;
    interval?: number;
  }>(),
  {
    interval: 1000,
  },
);
const now = ref(Date.now());
const endDate = computed(
  () => new Date(props.end.includes("T") ? props.end : `${props.end}T23:59:59`),
);
const finished = computed(() => endDate.value.getTime() < Date.now());
const finishesToday = computed(() => isToday(endDate.value));
const countdown = computed<Record<"hh" | "mm" | "ss", string> | null>(() => {
  if (!finishesToday.value) return null;
  const preEndDate = new Date(endDate.value);

  preEndDate.setTime(preEndDate.getTime() - 24 * 60 * 60 * 1000);

  if (
    now.value >= preEndDate.getTime() &&
    now.value < endDate.value.getTime()
  ) {
    let ss = endDate.value.getTime() - now.value;
    const hh = Math.floor(ss / (60 * 60 * 1000));
    ss -= hh * 60 * 60 * 1000;
    const mm = Math.floor(ss / (60 * 1000));
    ss -= mm * 60 * 1000;
    ss = Math.floor(ss / 1000);

    return {
      hh: hh.toString().padStart(2, "0"),
      mm: mm.toString().padStart(2, "0"),
      ss: ss.toString().padStart(2, "0"),
    };
  }

  return null;
});

onMounted(() => {
  useInterval(props.interval, {
    callback: () => {
      now.value = Date.now();
    },
  });
});
</script>

<template>
  <div class="action-date">
    <VText
      v-if="finished"
      class="action-date__simple"
      tag="div"
      color="system-warning"
      font="body-small-accent"
    >
      <WarningIcon /> Акция завершилась
    </VText>

    <div v-else-if="countdown" class="action-date__with-countdown">
      <VText tag="div" font="body-small-accent" class="action-date__simple">
        <SaleIcon /> До конца акции
      </VText>
      <div class="action-date__countdown">
        <VText
          font="body-large-accent"
          color="accent"
          class="action-date__digit"
        >
          {{ countdown.hh }}
        </VText>
        <VText font="body-large-accent" color="accent"> : </VText>
        <VText
          font="body-large-accent"
          color="accent"
          class="action-date__digit"
        >
          {{ countdown.mm }}
        </VText>
        <VText font="body-large-accent" color="accent"> : </VText>
        <VText
          font="body-large-accent"
          color="accent"
          class="action-date__digit"
        >
          {{ countdown.ss }}
        </VText>
      </div>
    </div>

    <VText
      v-else
      tag="div"
      class="action-date__simple"
      :font="{ xs: 'body-small-accent' }"
    >
      <VIcon color="project"><SaleIcon /></VIcon> До
      {{ formatDateToDDMMMM(end) }} ({{ getDayOfWeekString(endDate) }})
    </VText>
  </div>
</template>

<style lang="postcss" scoped>
.action-date {
  &__simple {
    display: flex;
    align-items: center;
    gap: var(--pl-unit-x2);
  }

  &__with-countdown {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  &__countdown {
    display: flex;
    align-items: center;
    gap: 2px;
  }

  &__digit {
    display: inline-block;
    width: var(--pl-unit-x6);
    height: var(--pl-unit-x6);
    border-radius: var(--pl-unit-x2);
    background-color: var(--pl-project-core-pale);
    text-align: center;
  }
}
</style>
